import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Stellt Meta-Tag-Overrides aus dem Backend bereit.
 *
 * Lädt EINMAL beim App-Start eine Map { '/path': {title, description, keywords, og_image} }
 * und cached sie in sessionStorage, damit auch SPA-Reloads schnell sind.
 *
 * SEO.js liest die Overrides via Hook + matched per Path.
 * Wenn der Master das Panel speichert, wird ein `seo:overrides-updated` Event
 * dispatchen, woraufhin der Provider neu lädt — Live-Wirkung ohne Reload.
 */
const SeoOverridesContext = createContext({ overrides: {}, getOverride: () => null, refresh: () => {} });

const CACHE_KEY = 'seo_overrides_v1';

export const SeoOverridesProvider = ({ children }) => {
  const [overrides, setOverrides] = useState(() => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const refresh = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/seo/meta-overrides/public`);
      const map = r.data?.overrides || {};
      setOverrides(map);
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(map)); } catch { /* ignore quota */ }
    } catch {
      // Backend offline oder keine Overrides — ignoriere stillschweigend,
      // SEO.js fällt automatisch auf Defaults zurück.
    }
  };

  useEffect(() => { refresh(); }, []);

  // Wenn der Master speichert, sofort neu laden (kein Reload nötig)
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('seo:overrides-updated', handler);
    return () => window.removeEventListener('seo:overrides-updated', handler);
  }, []);

  const getOverride = (path) => {
    if (!path) return null;
    // exakter Match zuerst
    if (overrides[path]) return overrides[path];
    // Fallback: ohne trailing slash matchen
    const norm = path.replace(/\/$/, '') || '/';
    return overrides[norm] || null;
  };

  const value = useMemo(() => ({ overrides, getOverride, refresh }), [overrides]);

  return <SeoOverridesContext.Provider value={value}>{children}</SeoOverridesContext.Provider>;
};

export const useSeoOverrides = () => useContext(SeoOverridesContext);
